"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { geoQueryOptions } from "../lib/react-query";
import type { GeoLocation, RadiusOption } from "../types/geo";
import type { GeoapifyFeature } from "../types/geoapify";
import { getDistanceBetweenTwoPoints } from "../utils/geoapify/geo-helpers";
import { useGeosearchState } from "./use-geosearch-state";
import { useLocale } from "./use-locale";
interface GeoSelectOption {
  feature?: GeoapifyFeature;
  geoLocation: GeoLocation;
  value: string;
}

// Utility: calculate bbox size from coordinates (polygon or bbox array)
function calculateBboxSizeFromCoords(coords: number[][] | number[][][]): number | undefined {
  // If it's a bbox: [minLon, minLat, maxLon, maxLat]
  if (Array.isArray(coords) && coords.length === 4 && typeof coords[0][0] !== "object") {
    const [minLon, minLat, maxLon, maxLat] = coords as unknown as [number, number, number, number];
    return getDistanceBetweenTwoPoints([minLat, minLon], [maxLat, maxLon]) * 1000;
  }
  // If it's a polygon: flatten and compute bbox
  const flat = (coords as number[][][]).flat(2);
  const allPoints: [number, number][] = [];
  for (let i = 0; i < flat.length; i += 2) {
    if (typeof flat[i] === "number" && typeof flat[i + 1] === "number") {
      allPoints.push([flat[i], flat[i + 1]]);
    }
  }
  if (allPoints.length >= 4) {
    const lats = allPoints.map(p => p[1]);
    const lngs = allPoints.map(p => p[0]);
    const bbox: [number, number, number, number] = [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)];
    return getDistanceBetweenTwoPoints([bbox[1], bbox[0]], [bbox[3], bbox[2]]) * 1000;
  }
  return;
}
export function useGeoSearch(t0) {
  const $ = _c(46);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    geosearchQueryData,
    radiusOptions,
    radiusOptionDefault,
    radiusOptionBoundaries,
    resultType
  } = t1;
  const locale = useLocale();
  const [queryState, setQueryState] = useGeosearchState();
  let t2;
  bb0: {
    const {
      location,
      lat,
      lng,
      placeId
    } = queryState;
    if (!location || lat === null || lng === null) {
      t2 = undefined;
      break bb0;
    }
    const t3 = placeId || undefined;
    let t4;
    if ($[2] !== lat || $[3] !== lng || $[4] !== location || $[5] !== t3) {
      t4 = {
        name: location,
        lat,
        lng,
        placeId: t3
      };
      $[2] = lat;
      $[3] = lng;
      $[4] = location;
      $[5] = t3;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    t2 = t4;
  }
  const geoLocation = t2;
  let t3;
  if ($[7] !== radiusOptions) {
    t3 = radiusOptions || [{
      value: 0,
      label: "0"
    }, {
      value: 5000,
      label: "5"
    }, {
      value: 25000,
      label: "25"
    }, {
      value: 50000,
      label: "50"
    }, {
      value: 100000,
      label: "100"
    }, {
      value: 300000,
      label: "300"
    }];
    $[7] = radiusOptions;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const RADIUS_OPTIONS = t3;
  const RADIUS_OPTION_DEFAULT = radiusOptionDefault || RADIUS_OPTIONS[2];
  const RADIUS_OPTION_BOUNDARIES = radiusOptionBoundaries || RADIUS_OPTIONS[0];
  let t4;
  bb1: {
    const param = queryState.radius;
    if (param !== null) {
      let t5;
      if ($[9] !== RADIUS_OPTIONS || $[10] !== param) {
        let t6;
        if ($[12] !== param) {
          t6 = r => r.value === param;
          $[12] = param;
          $[13] = t6;
        } else {
          t6 = $[13];
        }
        t5 = RADIUS_OPTIONS.find(t6);
        $[9] = RADIUS_OPTIONS;
        $[10] = param;
        $[11] = t5;
      } else {
        t5 = $[11];
      }
      const option = t5;
      if (option) {
        t4 = option;
        break bb1;
      }
    }
    t4 = RADIUS_OPTION_DEFAULT;
  }
  const radius = t4;
  let t5;
  if ($[14] !== geoLocation?.placeId || $[15] !== locale || $[16] !== radius.value) {
    t5 = geoQueryOptions.boundaries(geoLocation?.placeId, locale, radius.value === 0);
    $[14] = geoLocation?.placeId;
    $[15] = locale;
    $[16] = radius.value;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const {
    data: boundaries,
    isLoading: isLoadingBoundaries,
    error: boundariesError
  } = useQuery(t5);
  let t6;
  bb2: {
    if (boundaries && boundaries.coordinates.length > 0) {
      let t7;
      if ($[18] !== boundaries.coordinates) {
        t7 = calculateBboxSizeFromCoords(boundaries.coordinates);
        $[18] = boundaries.coordinates;
        $[19] = t7;
      } else {
        t7 = $[19];
      }
      t6 = t7;
      break bb2;
    }
    t6 = undefined;
  }
  const bboxSize = t6;
  let t7;
  if ($[20] !== queryState.radius || $[21] !== setQueryState) {
    t7 = (loc, newRadius) => {
      if (loc) {
        setQueryState({
          location: loc.name,
          lat: loc.lat,
          lng: loc.lng,
          placeId: loc.placeId || null,
          radius: newRadius ? newRadius.value : queryState.radius
        });
      } else {
        setQueryState({
          location: null,
          lat: null,
          lng: null,
          placeId: null,
          radius: null
        });
      }
    };
    $[20] = queryState.radius;
    $[21] = setQueryState;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const setGeoLocation = t7;
  let t8;
  if ($[23] !== setQueryState) {
    t8 = option_0 => {
      setQueryState({
        radius: option_0.value
      });
    };
    $[23] = setQueryState;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const setRadius = t8;
  let t9;
  if ($[25] !== RADIUS_OPTION_BOUNDARIES || $[26] !== RADIUS_OPTION_DEFAULT || $[27] !== radius.value || $[28] !== setGeoLocation) {
    t9 = value => {
      if (value) {
        const newRadius_0 = determineSearchMode(value.feature);
        setGeoLocation(value.geoLocation, newRadius_0);
      } else {
        setGeoLocation(undefined);
      }
      function determineSearchMode(feature) {
        const featureBboxSize = feature?.bbox ? calculateBboxSizeFromCoords(feature.bbox as unknown as number[][]) : undefined;
        if (!feature) {
          if (radius.value === 0) {
            return RADIUS_OPTION_DEFAULT;
          }
          return;
        }
        if (!feature.bbox) {
          return;
        }
        if (featureBboxSize !== undefined && (featureBboxSize < radius.value * 2 || featureBboxSize < 50000)) {
          if (radius.value === 0) {
            return RADIUS_OPTION_DEFAULT;
          }
          return;
        }
        return RADIUS_OPTION_BOUNDARIES;
      }
    };
    $[25] = RADIUS_OPTION_BOUNDARIES;
    $[26] = RADIUS_OPTION_DEFAULT;
    $[27] = radius.value;
    $[28] = setGeoLocation;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const handleGeoSelectChange = t9;
  let t10;
  if ($[30] !== geosearchQueryData) {
    t10 = filterGeoqueryResults(geosearchQueryData);
    $[30] = geosearchQueryData;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  const filteredOptions = t10;
  let t11;
  if ($[32] !== RADIUS_OPTION_BOUNDARIES || $[33] !== RADIUS_OPTION_DEFAULT || $[34] !== bboxSize || $[35] !== boundaries || $[36] !== boundariesError || $[37] !== filteredOptions || $[38] !== geoLocation || $[39] !== handleGeoSelectChange || $[40] !== isLoadingBoundaries || $[41] !== radius || $[42] !== resultType || $[43] !== setGeoLocation || $[44] !== setRadius) {
    t11 = {
      geoLocation,
      setGeoLocation,
      boundaries,
      isLoadingBoundaries,
      boundariesError,
      radius,
      setRadius,
      bboxSize,
      resultType,
      RADIUS_OPTION_DEFAULT,
      RADIUS_OPTION_BOUNDARIES,
      handleGeoSelectChange,
      filteredOptions
    };
    $[32] = RADIUS_OPTION_BOUNDARIES;
    $[33] = RADIUS_OPTION_DEFAULT;
    $[34] = bboxSize;
    $[35] = boundaries;
    $[36] = boundariesError;
    $[37] = filteredOptions;
    $[38] = geoLocation;
    $[39] = handleGeoSelectChange;
    $[40] = isLoadingBoundaries;
    $[41] = radius;
    $[42] = resultType;
    $[43] = setGeoLocation;
    $[44] = setRadius;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  return t11;
}
function filterGeoqueryResults(geosearchQueryData: {
  features: GeoapifyFeature[];
} | undefined): GeoSelectOption[] {
  if (!geosearchQueryData?.features) {
    return [];
  }
  const geoOptions = geosearchQueryData?.features.map((feature: GeoapifyFeature) => {
    const {
      properties
    } = feature;
    const name = [properties.city, properties.state, properties.country].filter(Boolean).join(", ");
    const geoLocation = {
      name,
      lat: feature.properties.lat,
      lng: feature.properties.lon,
      placeId: feature.properties.place_id
    };
    return {
      value: geoLocation.name,
      geoLocation,
      feature
    };
  });

  // Remove duplicates based on the 'value' property
  return Array.from(geoOptions.reduce((map: Map<string, GeoSelectOption>, obj: GeoSelectOption) => map.set(obj.value, obj), new Map()).values());
}